import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, type KpiReportDTO, type RateDTO } from '../lib/api'
import { ChannelMark } from '../components/icons'
import type { ChannelType } from '@sac/contracts'

/**
 * Relatórios — the KPI board.
 *
 * Its main job is refusing to state things it cannot support. Every rate arrives with the sample
 * it was computed from, and a rate the API suppressed is rendered as "dados insuficientes" with
 * the count, never as a dash or a zero. A board that shows 8,3% from twelve conversations is
 * worse than one that shows nothing there: people act on the number and stop asking for the
 * sample.
 */

const PERIODS: [number, string][] = [
  [7, '7 dias'],
  [30, '30 dias'],
  [90, '90 dias'],
]

function money(n: number) {
  return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** Minutes into something a person reads without converting in their head. */
function duration(min: number | null): string {
  if (min === null) return '—'
  if (min < 60) return `${min} min`
  const h = min / 60
  if (h < 48) return `${h.toFixed(1).replace('.', ',')} h`
  return `${Math.round(h / 24)} dias`
}

function Card({
  label,
  rate,
  detail,
  hint,
  tone,
}: {
  label: string
  rate: RateDTO
  detail?: string
  hint?: string
  tone?: 'ok' | 'warn'
}) {
  return (
    <div className={`kpi-card ${rate.value === null ? 'muted' : (tone ?? '')}`}>
      <div className="kpi-label">{label}</div>
      {rate.value === null ? (
        <>
          <div className="kpi-value insufficient">Dados insuficientes</div>
          <div className="kpi-sub">
            {rate.n} {rate.n === 1 ? 'caso' : 'casos'} — poucos para uma porcentagem confiável
          </div>
        </>
      ) : (
        <>
          <div className="kpi-value">{rate.value.toFixed(1).replace('.', ',')}%</div>
          <div className="kpi-sub">
            {detail ? `${detail} · ` : ''}base de {rate.n}
          </div>
        </>
      )}
      {hint && <div className="kpi-hint">{hint}</div>}
    </div>
  )
}

export function Relatorios() {
  const [days, setDays] = useState(30)
  const { data, isLoading } = useQuery({ queryKey: ['kpis', days], queryFn: () => api.kpis(days) })

  if (isLoading) return <div className="empty">Carregando…</div>
  if (!data) return <div className="empty">Sem dados.</div>

  const k: KpiReportDTO = data
  const ia = k.ia

  return (
    <div className="crm">
      <div className="crm-head">
        <h2>Relatórios</h2>
        <p>
          Cada indicador mostra a base sobre a qual foi calculado. Quando a amostra é pequena
          demais, o número não aparece — uma porcentagem sobre poucos casos parece um fato e não é.
        </p>
      </div>

      <div className="crm-filters">
        {PERIODS.map(([d, label]) => (
          <button key={d} className={`btn sec sm ${days === d ? 'on' : ''}`} onClick={() => setDays(d)}>
            {label}
          </button>
        ))}
      </div>

      <div className="kpi-grid">
        <Card
          label="Assertividade da IA"
          rate={k.assertividade}
          detail={`${k.assertividade.verbatim} enviadas sem editar`}
          hint={`${k.assertividade.edited} editadas · ${k.assertividade.rejected} descartadas`}
          tone="ok"
        />
        <Card
          label="Perguntas sem conhecimento"
          rate={k.lacunaConhecimento}
          detail={`${k.lacunaConhecimento.gaps} lacunas`}
          hint="A IA pediu um dado em vez de arriscar uma resposta — é a lista de lacunas a preencher."
          tone="warn"
        />
        <Card
          label="Conversão de pré-venda"
          rate={k.conversao.preVenda}
          detail={`${k.conversao.preVenda.converted} vendas · ${money(k.conversao.preVenda.revenue)}`}
          hint={
            k.conversao.preVenda.stillOpen > 0
              ? `${k.conversao.preVenda.stillOpen} conversas ainda dentro da janela de 30 dias, fora da conta`
              : 'Somente conversas cuja janela de 30 dias já fechou'
          }
        />
        <div className="kpi-card">
          <div className="kpi-label">Volume</div>
          <div className="kpi-value">{k.volume.inbound}</div>
          <div className="kpi-sub">mensagens recebidas em {k.volume.conversations} conversas</div>
        </div>
      </div>

      <div className="cfg-block">
        <div className="blk-h">Tempo de resposta pelo SAC</div>
        <p className="cfg-note" style={{ padding: '0 14px 8px', margin: 0 }}>
          Só conta respostas enviadas por aqui. O que foi respondido no Seller Centre aparece à
          parte — não é fila, é trabalho feito em outro lugar.
        </p>
        <div className="crm-table">
          <div className="crm-row rt-row crm-th">
            <span>Canal</span>
            <span>Respondidas</span>
            <span>Mediana</span>
            <span>p90</span>
            <span>Fora do SAC</span>
            <span>Sem resposta</span>
          </div>
          {k.responseTime.viaSac.map((c) => (
            <div className="crm-row rt-row" key={c.channelType}>
              <span>
                <ChannelMark channel={c.channelType as ChannelType} size={15} /> {c.channelType}
              </span>
              <span>{c.answered}</span>
              <span>
                {duration(c.medianMinutes)}
                {c.medianMinutes === null && c.answered > 0 && (
                  <span className="cfg-badge">amostra pequena</span>
                )}
              </span>
              <span>{duration(c.p90Minutes)}</span>
              <span className="t2">{c.answeredElsewhere}</span>
              <span className={c.unanswered > 0 ? 'kpi-warn' : 't2'}>{c.unanswered}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="cfg-block">
        <div className="blk-h">Vendas após atendimento com IA</div>
        <p className="cfg-note" style={{ padding: '0 14px 8px', margin: 0 }}>
          Comparado com as respostas escritas sem sugestão, na mesma janela de {ia.windowDays} dias.
          É <b>após</b>, não <b>por causa de</b>: não há grupo de controle, e parte dessas pessoas
          compraria de qualquer forma.
        </p>
        <div className="crm-table">
          <div className="crm-row ia-row crm-th">
            <span>Grupo</span>
            <span>Conversas</span>
            <span>Converteram</span>
            <span>Taxa</span>
            <span>Receita</span>
          </div>
          {([
            ['Com sugestão da IA', ia.assisted],
            ['Sem sugestão', ia.unassisted],
          ] as const).map(([label, g]) => (
            <div className="crm-row ia-row" key={label}>
              <span>{label}</span>
              <span>{g.conversations}</span>
              <span>{g.converted}</span>
              <span>
                {/* Same floor as the cards, applied here too: comparing 8,3% from twelve
                    conversations against 28,8% from seven hundred invites a false conclusion. */}
                {g.conversations >= 30 ? `${g.conversionRate.toFixed(1).replace('.', ',')}%` : '—'}
              </span>
              <span>{money(g.revenue)}</span>
            </div>
          ))}
        </div>
        {ia.assisted.conversations < 30 && (
          <p className="cfg-note" style={{ padding: '8px 14px 0', margin: 0 }}>
            A IA ainda respondeu poucas conversas ({ia.assisted.conversations}) para que a
            comparação signifique alguma coisa. O número aparece quando a base crescer.
          </p>
        )}
      </div>
    </div>
  )
}
